<template>
    <Widget title="Scheduled Announcements">
        <template v-slot:icon>
            <i class="fa-solid fa-calendar" style="font-size:30px; color: #036"></i>
        </template>
        <div id="main-container">
            <ContentSwitcher id="content-container" :currentContent="currentContent">
                <template #select-types>
                    <h4>Set Up Your Announcement</h4>
                    <div id="select-types-container">
                        <p class="mb-1">What would you like to include in your announcement?</p>
                        <div class="form-group d-flex flex-column justify-content-between mx-3">
                            <div class="form-check">
                                <input type="checkbox" id="chkAudio" class="form-check-input" v-model="selectedTypes.audio" />
                                <label class="form-check-label" for="chkAudio">Play Sound</label>
                            </div>
                            <p class="mb-2">Choose a sound for a specific area.
                                You can<span v-if="canRecordAudio"> record or</span> upload a new one, or pick from sounds you already have.
                            </p>

                            <span class="g2tooltip" :data-tooltip-off="feature_visual_alert_enabled">
                                <span class="g2tooltiptext">
                                    <p>Visual Alerts must be enabled to use this feature.</p>
                                    <p><strong>For more information, please visit our website at <a href="https://www.galaxynext.us/">https://www.galaxynext.us/</a>, or contact us at <a href="mailto:sales@galaxynext.us">sales@galaxynext.us</a> to get started.</strong></p>
                                </span>
                                <div class="form-check">
                                    <input type="checkbox" id="chkVisual" class="form-check-input" v-model="selectedTypes.visual" :disabled="!feature_visual_alert_enabled"/>
                                    <label class="form-check-label" for="chkVisual">Show a Visual</label>
                                </div>
                                <p class="mb-2" :disabled="!feature_visual_alert_enabled">Display a visual in a chosen area. Use an existing visual or add text to a ready-made template.</p>
                            </span>

                            <div class="form-check" v-if="machine_type === 'Box'">
                                <input type="checkbox" id="chkText" class="form-check-input" v-model="selectedTypes.text" />
                                <label class="form-check-label" for="chkText">Display a Message</label>
                            </div>
                            <p class="mb-2" v-if="machine_type === 'Box'">Share a text message in a specific area. It'll show up on all G2 IP Speaker devices that can display text.</p>
                        </div>
                    </div>
                </template>

                <template #create-audio>
                    <h4>Setup Your Sound</h4>
                    <div class="section">
                        <div class="section-heading">
                            <div class="form-check form-check-inline" v-if="canRecordAudio">
                                <input class="form-check-input" type="radio" name="selectedAudioType" id="audioRecord" value="record"
                                v-model="selectedAudioType" @click="selectedAudio = null" :disabled="disabled">
                                <label class="form-check-label" for="audioRecord">Record new</label>
                            </div>
                            <div class="form-check form-check-inline">
                                <input class="form-check-input" type="radio" name="selectedAudioType" id="audioExisting" value="existing"
                                v-model="selectedAudioType" @click="selectedAudio = null" :disabled="disabled">
                                <label class="form-check-label" for="audioExisting">Use existing</label>
                            </div>
                            <div class="form-check form-check-inline">
                                <input class="form-check-input" type="radio" name="selectedAudioType" id="audioFile" value="upload"
                                v-model="selectedAudioType" @click="selectedAudio = null" :disabled="disabled">
                                <label class="form-check-label" for="audioFile">Select file</label>
                            </div>
                        </div>

                        <div class="section-content">
                            <div v-if="selectedAudioType == 'record'">
                                <RecordAndPlayback
                                    @recordstart="handleRecordStart"
                                    @recordend="handleRecordEnd"
                                    v-model="selectedAudio"
                                />
                                <small class="form-text mx-2">Record up to 15 minutes of voice audio.</small>
                            </div>
                            <div v-if="selectedAudioType == 'existing'">
                                <SoundAndPlayback v-model="selectedAudio" />
                            </div>
                            <div v-if="selectedAudioType == 'upload'">
                                <BrowseAndPlayback v-model="selectedAudio" />
                            </div>
                            <h5 class="mt-4">Choose a zone where the sound will be played</h5>
                            <ZoneSelect :zones="audioZones" placeholder="Select a zone..." v-model="selectedAudioZone" />
                        </div>

                    </div>
                </template>

                <template #create-visual>
                    <h4>Setup Your Visual</h4>
                    <div class="section">
                        <div class="section-heading">
                            <div class="form-check form-check-inline">
                                <input class="form-check-input" type="radio" name="selectedVisualType" id="visualExisting" value="existing"
                                @click="selectedVisual = null" v-model="selectedVisualType">
                                <label class="form-check-label" for="visualExisting">Use existing</label>
                            </div>
                            <div class="form-check form-check-inline">
                                <input class="form-check-input" type="radio" name="selectedVisualType" id="visualTemplate" value="template"
                                @click="selectedVisual = null" v-model="selectedVisualType">
                                <label class="form-check-label" for="visualTemplate">Create using template</label>
                            </div>
                        </div>

                        <div class="section-content">
                            <div v-if="selectedVisualType == 'existing'">
                                <h5>Choose a visual</h5>
                                <VisualSelect :visuals="filteredVisuals" placeholder="Select a visual..." v-model="selectedVisual" />
                            </div>
                            <div v-if="selectedVisualType == 'template'">
                                <h5>Choose a visual template</h5>
                                <VisualSelect :visuals="templateVisuals" placeholder="Select a template..." v-model="selectedVisual" />
                                <div class="form-group my-3">
                                    <h5>Write a message to display</h5>
                                    <input type="text" class="form-control" id="txtPlaceholder" placeholder="Enter placeholder text..." v-model="visualPlaceholder"/>
                                </div>
                            </div>
                            <h5 class="mt-3">Choose a zone where the visual will be shown</h5>
                            <ZoneSelect :zones="visualZones" placeholder="Select a zone..." v-model="selectedVisualZone" />
                        </div>
                    </div>
                </template>

                <template #create-text>
                    <h4>Setup Your Message</h4>
                    <div class="section">
                        <div class="section-content">
                            <div class="form-group my-1">
                                <h5>Write a message to display</h5>
                                <textarea class="form-control" id="txtMessage" placeholder="Enter message..." maxlength="1023" v-model="selectedText"></textarea>
                            </div>
                            <h5 class="mt-4">Choose a zone where the message will be shown</h5>
                            <ZoneSelect :zones="audioZones" placeholder="Select a zone..." v-model="selectedTextZone" />
                        </div>
                    </div>
                </template>

                <template #schedule-announcement>
                    <h4>Schedule the Announcement</h4>
                    <div class="d-flex flex-column">
                        <div class="form-group my-2">
                            <h5>Date and time to play announcement</h5>
                            <input id="schedule-date" type="datetime-local" class="form-control form-control-inline" v-model="schedule_date"/>
                        </div>
                        <div v-if="selectedTypes.visual || selectedTypes.text" class="form-group mt-3">
                            <h5>Duration to display the {{ selectedTypes.visual ? 'visual' : ''}} {{ selectedTypes.visual && selectedTypes.text ? 'and' : '' }} {{ selectedTypes.text ? 'text' : '' }} (in seconds).</h5>
                            <div class="align-items-center">
                                <input id="pause-duration" type="number" min="0" max="600" step="10" class="form-control" v-model.number="pause_duration"/>
                                <small v-if="selectedTypes.audio" class="form-text text-muted">The duration will always be at least the length of the sound.</small>
                            </div>
                        </div>
                        <h5 v-if="scheduledEvents.length > 0" class="mt-2">Currently scheduled announcements</h5>
                        <h5 v-else class="mt-2">There are no announcements currently scheduled</h5>
                        <ScheduleList
                            :style="{ 'max-height': '24vh' }"
                            :events="scheduledEvents" @eventdelete="handleEventDelete" @reschedule="handleEventReschedule">
                            <template v-slot="{ event }">
                                <span class="event-summary mx-4">
                                    <i v-if="event_summaries[event.event_id] && event_summaries[event.event_id].sounds" class="fa-solid fa-volume-up mx-2"></i>
                                    <i v-if="event_summaries[event.event_id] && event_summaries[event.event_id].visuals" class="fa-solid fa-image mx-2"></i>
                                    <i v-if="event_summaries[event.event_id] && event_summaries[event.event_id].texts" class="fa-solid fa-message mx-2"></i>
                                </span>
                            </template>
                        </ScheduleList>
                    </div>
                </template>

                <template #view-schedule>
                    <h4>Current Announcement Schedule</h4>
                    <p v-if="scheduledEvents.length === 1">{{ `There is ${scheduledEvents.length} announcement scheduled to play at the following time:` }}</p>
                    <p v-else-if="scheduledEvents.length > 1">{{ `There are ${scheduledEvents.length} announcements scheduled to play at the following times:` }}</p>
                    <p v-else>There are currently no announcements scheduled</p>
                    <ScheduleList :style="{ 'max-height': '33vh' }" :events="scheduledEvents" @eventdelete="handleEventDelete" @reschedule="handleEventReschedule" >
                        <template v-slot="{ event }">
                            <span class="event-summary mx-4">
                                <i v-if="event_summaries[event.event_id] && event_summaries[event.event_id].sounds" class="fa-solid fa-volume-up mx-2"></i>
                                <i v-if="event_summaries[event.event_id] && event_summaries[event.event_id].visuals" class="fa-solid fa-image mx-2"></i>
                                <i v-if="event_summaries[event.event_id] && event_summaries[event.event_id].texts" class="fa-solid fa-message mx-2"></i>
                            </span>
                        </template>
                    </ScheduleList>
                </template>

            </ContentSwitcher>
            
            <div id="buttons-container">
                <button v-if="currentContent === 'select-types'" type="button" class="btn btn-hub-primary" @click="showSchedule">Show existing schedule</button>
                <button v-if="currentContent !== 'select-types'" type="button" class="btn btn-hub-primary mr-auto" @click="showPrevContent" :disabled="disabled">{{ currentContent === 'view-schedule' ? 'Schedule a new announcement' : 'Back' }}</button>
                <button v-if="currentContent !== 'schedule-announcement' && currentContent !== 'view-schedule'" type="button" class="btn btn-hub-primary ml-auto" @click="showNextContent" :disabled="disabled">Next</button>
                <button v-if="currentContent === 'schedule-announcement'" type="button" class="btn btn-hub-primary ml-auto" @click="handleSubmit" :disabled="disabled">Schedule Announcement</button>
            </div>
        </div>
    </Widget>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment';
import RecordAndPlayback from '../../Sound/RecordAndPlayback.vue'
import BrowseAndPlayback from '../../Sound/BrowseAndPlayback.vue'
import SoundAndPlayback from '../../Sound/SoundAndPlayback.vue'
import Widget from '../Widget.vue'
import ZoneSelect from '../../Zone/ZoneSelect.vue'
import VisualSelect from '../../Visual/VisualSelect.vue'
import { EVENT_TYPE } from '../../../assets/js/resource-types'
import ContentSwitcher from '../../Utilities/ContentSwitcher.vue';
import ScheduleList from './ScheduleList.vue';

export default {
    components: { Widget, RecordAndPlayback, BrowseAndPlayback, SoundAndPlayback, ZoneSelect, VisualSelect, ContentSwitcher, ScheduleList },
    data() {
        return {
            selectedTypes: {
                audio: true,
                visual: false,
                text: false
            },
            selectedAudioType: 'existing',
            selectedAudio: null,
            selectedAudioZone: null,

            selectedVisualType: 'existing',
            selectedVisual: null,
            selectedVisualZone: null,
            visualPlaceholder: '',

            selectedTextZone: null,
            selectedText: '',

            schedule_date: moment(this.current_server_time).format('YYYY-MM-DDTHH:mm'),
            pause_duration: 10,

            currentContent: 'view-schedule',
            disabled: false
        }
    },
    computed: {
        ...mapGetters({
            zones: 'allZones',
            events: 'allEvents',
            visuals: 'allAdvancedVisuals',
            sounds: 'allSounds',
            event_summaries: 'allEventSummaries',
            event_steps: 'allEventSteps',
            current_server_time: 'currentServerTime',
            feature_visual_alert_enabled: 'featureVisualAlertEnabled',
            machine_type: 'machineType'
        }),
        audioZones() {
            return this.zones.filter(zone => zone.zone_type_id !== 4)
        },
        visualZones() {
            return this.zones.filter(zone => zone.zone_type_id === 4)
        },
        filteredVisuals() {
            return this.visuals.filter(visual => this.announcementEventIds.includes(visual.event_id))
        },
        templateVisuals() {
            return this.visuals.filter(visual => visual.event_id === this.templateVisualEventId)
        },
        announcementEventIds() {
            return this.events.filter(e => e.event_type_id === EVENT_TYPE.Announcement).map(e => e.event_id)
        },
        scheduledEvents() {
            return this.events.filter(e => e.event_type_id === EVENT_TYPE.Scheduled)
        },
        templateVisualEventId() {
            return this.events.filter(e => e.event_type_id === EVENT_TYPE.System).find(e => e.name === 'Template Visuals').event_id
        },
        canRecordAudio() {
            return isSecureContext
        }
    },
    watch: {
        scheduledEvents(newEvents) {
            if(newEvents.length === 0) {
                this.currentContent = 'select-types'
            }
        }
    },
    mounted() { 
        this.$store.dispatch('getAdvancedVisuals')
        this.$store.dispatch('getEventSummaries')
    },
    methods: {
        showNextContent() {
            switch(this.currentContent) {
                case 'select-types':
                    if(this.selectedTypes.audio) {
                        this.currentContent = 'create-audio'
                    } else if(this.selectedTypes.visual) {
                        this.currentContent = 'create-visual'
                    } else if(this.selectedTypes.text) {
                        this.currentContent = 'create-text'
                    }
                    break
                case 'create-audio':
                    if(this.validateAudio())  {
                        if(this.selectedTypes.visual) {
                            this.currentContent = 'create-visual'
                        } else if(this.selectedTypes.text) {
                            this.currentContent = 'create-text'
                        } else {
                            this.schedule_date = moment(this.current_server_time).format('YYYY-MM-DDTHH:mm')
                            this.currentContent = 'schedule-announcement'
                        }
                    }
                    break
                case 'create-visual':
                    if(this.validateVisual()) {
                        if(this.selectedTypes.text) {
                            this.currentContent = 'create-text'
                        } else {
                            this.schedule_date = moment(this.current_server_time).format('YYYY-MM-DDTHH:mm')
                            this.currentContent = 'schedule-announcement'
                        }
                    }
                    break
                case 'create-text':
                    if(this.validateText()) {
                        this.schedule_date = moment(this.current_server_time).format('YYYY-MM-DDTHH:mm')
                        this.currentContent = 'schedule-announcement'
                    }
                    break
                case 'schedule-announcement':
                default:
                    break
            }
        },
        showPrevContent() {
            switch(this.currentContent) {
                case 'schedule-announcement':
                    if(this.selectedTypes.text) {
                        this.currentContent = 'create-text'
                    } else if(this.selectedTypes.visual) {
                        this.currentContent = 'create-visual'
                    } else if(this.selectedTypes.audio) {
                        this.currentContent = 'create-audio'
                    }
                    break;
                case 'create-text':
                    if(this.selectedTypes.visual) {
                        this.currentContent = 'create-visual'
                    } else if(this.selectedTypes.audio) {
                        this.currentContent = 'create-audio'
                    } else {
                        this.currentContent = 'select-types'
                    }
                    break;
                case 'create-visual':
                    if(this.selectedTypes.audio) {
                        this.currentContent = 'create-audio'
                    } else {
                        this.currentContent = 'select-types'
                    }
                    break;
                case 'create-audio':
                case 'view-schedule':
                    this.currentContent = 'select-types'
                    break;
                case 'select-types':
                default:
                    break
            }
        },
        showSchedule() {
            this.currentContent = 'view-schedule'
        },
        handleRecordStart() {
            this.disabled = true
        },
        handleRecordEnd(audioFile) {
            this.disabled = false
        },
        handleEventDelete(event) {
            this.g2dialog.$confirm("You are about to delete a scheduled announcement. Continue?").then(confirmed => {
                confirmed && this.$store.dispatch('deleteEvent', event.event_id).then((success) => {
                    if(success) {
                        this.g2dialog.$alert("Announcement deleted")
                    } else {
                        this.g2dialog.$alert("Unable to delete announcement")
                    }
                })
            })
        },
        handleEventReschedule(event, schedule_date) {
            // Check if we need to update the 'delete_after' field for a sound
            let stepPromise = null, soundPromise = null
            const hasSound = this.event_summaries[event.event_id] && this.event_summaries[event.event_id].sounds
            if(hasSound) {
                soundPromise = this.$store.dispatch('getSounds')
                stepPromise = this.$store.dispatch('getEventSteps', event.event_id)
            } else {
                soundPromise = Promise.resolve()
                stepPromise = Promise.resolve()
            }

            this.$store.dispatch('addEditEvent', { event: { ...event, name: schedule_date }}).then((success) => {
                if(success) {
                    Promise.all([soundPromise, stepPromise]).then(() => {
                        let soundUpdatePromise = null
                        if(hasSound) {
                            const soundStep = this.event_steps.find(s => s.event_step_command_type_id === 1)
                            if(soundStep) {
                                const sound = this.sounds.find(s => s.sound_id === soundStep.sound_id)
                                if(sound.scheduled) {
                                    sound.remove_after = moment(schedule_date, 'YYYY-MM-DDTHH:mm').add(2, 'weeks').toISOString().substring(0, 16)
                                    soundUpdatePromise = this.$store.dispatch('updateSound', sound)
                                }
                            }
                        }
                        if(!soundUpdatePromise) soundUpdatePromise = Promise.resolve()

                        soundUpdatePromise.then(() => {
                            this.$store.dispatch('getEvents')
                            this.g2dialog.$alert('Announcement rescheduled successfully')
                        })
                    })
                } else {
                    this.g2dialog.$alert('Unable to reschedule announcement')
                }
            })
        },
        validateAudio() {
            if(this.selectedTypes.audio) {
                if(!this.selectedAudio) {
                    this.g2dialog.$alert("Please select a sound")
                    return false
                }
                if(!this.selectedAudioZone) {
                    this.g2dialog.$alert("Please select an audio zone")
                    return false
                }
            }
            return true
        },
        validateVisual() {
            if(this.selectedTypes.visual) {
                if(!this.selectedVisual) {
                    this.g2dialog.$alert("Please select a visual")
                    return false
                }
                if(!this.selectedVisualZone) {
                    this.g2dialog.$alert("Please select a visual zone")
                    return false
                }
                if(this.selectedVisualType == 'template' && !this.visualPlaceholder) {
                    this.g2dialog.$alert("Please enter message for visual")
                    return false
                } 
            }
            return true
        },
        validateText() {
            if(this.selectedTypes.text) {
                if(!this.selectedText) {
                    this.g2dialog.$alert("Please enter message for text devices")
                    return false
                }
                if(!this.selectedTextZone) {
                    this.g2dialog.$alert("Please select a zone for messages")
                    return false
                }
            }
            return true
        },
        validateSchedule() {
            if(!this.schedule_date) {
                this.g2dialog.$alert('Please enter the schedule date and time')
                return false
            }

            const minimum_scheduled = moment().add(2, 'minutes')
            const scheduled = moment(this.schedule_date, 'YYYY-MM-DDTHH:mm')
            if(scheduled.isSameOrBefore(minimum_scheduled)) {
                this.g2dialog.$alert('Announcements must be scheduled at least two minutes after the current time.')
                return false
            }
            
            return true
        },
        reset() {
            this.selectedTypes = {
                audio: true,
                visual: false,
                text: false
            },
            this.selectedAudioType = 'existing',
            this.selectedAudio = null,
            this.selectedAudioZone = null,

            this.selectedVisualType = 'existing',
            this.selectedVisual = null,
            this.selectedVisualZone = null,
            this.visualPlaceholder = '',

            this.selectedTextZone = null,
            this.selectedText = '',

            this.schedule_date = moment().format('YYYY-MM-DDTHH:mm'),
            this.pause_duration = 10,

            this.currentContent = 'view-schedule',
            this.disabled = false
        },
        handleSubmit() {
            if(!this.validateSchedule()) {
                return
            }

            const name = this.schedule_date
            let remove_after = null
            
            if(this.selectedTypes.audio && this.selectedAudioType === 'record') {
                this.selectedAudio = new File([this.selectedAudio], `scheduled-audio-${this.schedule_date.replace(':', '-')}.wav`, { type: 'audio/wav' })
                remove_after = moment(this.schedule_date, 'YYYY-MM-DDTHH:mm').add(2, 'weeks').toISOString().substring(0, 16)
            }

            let audioPromise = this.selectedTypes.audio && this.selectedAudioType !== 'existing' ?
                    this.$store.dispatch('uploadSoundFile', { file: this.selectedAudio, tags: { scheduled: this.selectedAudioType === 'record', announcement: this.selectedAudioType !== 'record' }, remove_after }) : Promise.resolve()

            audioPromise.then(audio => {
                this.$store.dispatch('addEditEvent', { event: {
                    event_id: 0,
                    event_type_id: EVENT_TYPE.Scheduled,
                    event_action_type_id: 3,
                    name: name,
                    description: '',
                    extension: '9',
                    input_number: 1,
                    device_id: null,
                    enabled: 1,
                    chat_enabled: false
                } }).then(event => {
                    if(!event) return
                    let stepOrder = 1
                    let visualId = null

                    let visualPromise = this.selectedTypes.visual ? this.$store.dispatch('addEditAdvancedVisual', { advanced_visual: {
                        ...this.selectedVisual,
                        advanced_visual_id: 0,
                        event_id: event.event_id
                    } }) : Promise.resolve()

                    visualPromise.then((visual) => {
                        visualId = visual && visual.advanced_visual_id
                        if(this.selectedTypes.audio) {
                            if(this.selectedAudioType !== 'existing') {
                                return this.$store.dispatch('addEditEventStep', { event_step: {
                                    event_step_id: 0,
                                    event_id: event.event_id,
                                    event_step_order_type_id: 1,
                                    event_step_command_type_id: 1,
                                    zone_id: this.selectedAudioZone.zone_id,
                                    sound_type_id: 1,
                                    sound_id: 0,
                                    sound: audio,
                                    advanced_visual_id: 0,
                                    barix_command: '',
                                    message: '',
                                    display_text: '',
                                    flasher_type: 'simultaneously',
                                    event_step_phone_numbers: [],
                                    sleep_duration: 0,
                                    to_event_step_id: 0,
                                    order: stepOrder++
                                } })
                            }
                            return this.$store.dispatch('addEditEventStep', { event_step: {
                                event_step_id: 0,
                                event_id: event.event_id,
                                event_step_order_type_id: 1,
                                event_step_command_type_id: 1,
                                zone_id: this.selectedAudioZone.zone_id,
                                sound_type_id: 1,
                                sound_id: 0,
                                sound: this.selectedAudio,
                                advanced_visual_id: 0,
                                barix_command: '',
                                message: '',
                                display_text: '',
                                flasher_type: 'simultaneously',
                                event_step_phone_numbers: [],
                                sleep_duration: 0,
                                to_event_step_id: 0,
                                order: stepOrder++
                            } })
                        } 
                    }).then(() => {
                        if(this.selectedTypes.visual) {
                            return this.$store.dispatch('addEditEventStep', { event_step: {
                                event_step_id: 0,
                                event_id: event.event_id,
                                event_step_order_type_id: 1,
                                event_step_command_type_id: 7,
                                zone_id: this.selectedVisualZone.zone_id,
                                sound_type_id: 0,
                                sound_id: 0,
                                sound: '',
                                advanced_visual_id: visualId,
                                barix_command: '',
                                message: '',
                                display_text: this.visualPlaceholder,
                                flasher_type: 'simultaneously',
                                event_step_phone_numbers: [],
                                sleep_duration: this.pause_duration,
                                to_event_step_id: 0,
                                order: stepOrder++
                            } })
                        } 
                    }).then(() => {
                        if(this.selectedTypes.text) {
                            return this.$store.dispatch('addEditEventStep', { event_step: {
                                event_step_id: 0,
                                event_id: event.event_id,
                                event_step_order_type_id: 1,
                                event_step_command_type_id: 11,
                                zone_id: this.selectedTextZone.zone_id,
                                sound_type_id: 0,
                                sound_id: 0,
                                sound: '',
                                advanced_visual_id: 0,
                                barix_command: '',
                                message: '',
                                display_text: this.selectedText,
                                flasher_type: 'simultaneously',
                                event_step_phone_numbers: [],
                                sleep_duration: this.pause_duration,
                                to_event_step_id: 0,
                                order: stepOrder++
                            } })
                        }
                    }).then(() => {
                        this.$store.dispatch('getEventSummaries')
                        this.g2dialog.$alert("Announcement has been successfully scheduled")
                        this.reset()
                    }).catch(() => {
                        this.g2dialog.$alert("Failed to create one or more steps")
                    })
                }).then(success => {
                    if(success === false) {
                        this.g2dialog.$alert("Unable to schedule the announcement for the selected time")
                    }
                })
            })
        }
    }
}
</script>

<style scoped>
#main-container {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    height: 100%;
}

.section {
    border: 1px solid #e0e0e0;
    background-color: #f5f5f5;
    min-height: 20rem;
    padding: 1rem;
}

.section-heading {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
    align-items: center;
    justify-content: start;
}

#content-container {
    flex: 12;
}

#buttons-container {
    flex: 1;
    display: flex;
    justify-content: flex-end;
    margin-top: 0.5rem;
}

#select-types-container .form-check {
    margin-bottom: 0.5rem;
}
#select-types-container .form-check-input {
    top: 0.7rem;
    scale: 1.4;
}

#select-types-container .form-check-label {
    margin-top: 0.5rem;
    margin-left: 0.5rem;
    font-size: 1.1rem;
}

#select-types-container p[disabled] {
    color: #888;
}

#select-types-container *[disabled]:hover *, 
#select-types-container *[disabled]:hover {
    cursor: pointer;
}

#txtMessage {
    resize: vertical;
    max-height: 150px;
    min-height: 4rem;
}
</style>